/**
 * P3-42: ChallengeBtn —— 单个挑战按钮。
 *
 * 逐行为对齐 `js/challenges.js:829-900`：
 * - 名称/描述/显隐/可用性由 `ChallengeBtnController.fetchModel` 直传
 * - 点击调用 `controller.buyItem(modelRef.current, event)` 后 `game.render()`
 * - tooltip 直接返回 description（挑战按钮无价格明细）
 */
import { useMemo, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import { useButtonModel, shallowSnapEq } from "../bonfire/useButtonModel";
import type { ModernButtonController } from "../bonfire/useButtonModel";

interface ChallengeMeta {
  name: string;
}

interface ChallengeBtnSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
}

function toSnapshot(model: Record<string, unknown>): ChallengeBtnSnapshot {
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: Boolean(model.visible),
    enabled: Boolean(model.enabled),
  };
}

export default function ChallengeBtn({ challenge }: { challenge: ChallengeMeta }) {
  const controller = useMemo<ModernButtonController | null>(() => {
    const ControllerClass = (
      window as unknown as {
        classes?: { ui?: { ChallengeBtnController?: new (game: GamePage) => ModernButtonController } };
      }
    ).classes?.ui?.ChallengeBtnController;
    const g = getGame();
    if (typeof ControllerClass !== "function" || !g) {
      return null;
    }
    return new ControllerClass(g);
  }, []);

  const { snap, modelRef } = useButtonModel(
    controller,
    () => ({ id: challenge.name }),
    toSnapshot,
    shallowSnapEq,
  );

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      getTooltip={() => snap.description}
      onClick={(event: MouseEvent) => {
        const result = controller?.buyItem?.(modelRef.current, event);
        if (result?.itemBought) {
          getGame()?.render();
        }
      }}
    />
  );
}
